import api from './api';

/**
 * MCQ Service
 *
 * Handles all MCQ-related API calls including:
 * - Admin MCQ management (CRUD)
 * - Admin question management
 * - User MCQ taking and submission
 * - Results and scorecard retrieval
 */

const mcqService = {
  // ===== ADMIN MCQ MANAGEMENT =====

  /**
   * Get all MCQs for a course (Admin only)
   * @param {number} courseId - Course ID
   * @returns {Promise<Array>} List of MCQs with question_count
   */
  getAllMCQs: async (courseId) => {
    const response = await api.get(`/mcqs/?course_id=${courseId}`);
    return response.data;
  },

  /**
   * Get MCQ details by ID
   * @param {number} id - MCQ ID
   * @returns {Promise<Object>} MCQ with questions
   */
  getMCQById: async (id) => {
    const response = await api.get(`/mcqs/${id}/`);
    return response.data;
  },

  /**
   * Create a new MCQ (Admin only)
   * @param {Object} mcqData - MCQ data
   * @param {number} mcqData.course_id - Parent course ID
   * @param {string} mcqData.title - MCQ title
   * @param {string} [mcqData.description] - MCQ description
   * @param {boolean} [mcqData.is_preview] - Whether MCQ is free preview
   * @param {number} [mcqData.order] - MCQ order
   * @returns {Promise<Object>} Created MCQ
   */
  createMCQ: async (mcqData) => {
    const response = await api.post('/mcqs/', mcqData);
    return response.data;
  },

  /**
   * Update an existing MCQ (Admin only)
   * @param {number} id - MCQ ID
   * @param {Object} mcqData - Updated MCQ data
   * @returns {Promise<Object>} Updated MCQ
   */
  updateMCQ: async (id, mcqData) => {
    const response = await api.put(`/mcqs/${id}/`, mcqData);
    return response.data;
  },

  /**
   * Delete an MCQ (Admin only)
   * @param {number} id - MCQ ID
   * @returns {Promise<void>}
   */
  deleteMCQ: async (id) => {
    await api.delete(`/mcqs/${id}/`);
  },

  /**
   * Reorder MCQs in bulk (Admin only)
   * @param {Array<number>} mcqIds - Array of MCQ IDs in desired order
   * @returns {Promise<Object>} Success message
   */
  reorderMCQs: async (mcqIds) => {
    const response = await api.post('/mcqs/reorder/', { mcq_ids: mcqIds });
    return response.data;
  },

  // ===== ADMIN QUESTION MANAGEMENT =====

  /**
   * Get all questions for an MCQ (Admin only)
   * @param {number} mcqId - MCQ ID
   * @returns {Promise<Array>} List of questions with correct answers
   */
  getQuestionsByMCQ: async (mcqId) => {
    const response = await api.get(`/questions/?mcq_id=${mcqId}`);
    return response.data;
  },

  /**
   * Create a new question (Admin only)
   * @param {Object} questionData - Question data
   * @param {number} questionData.mcq_id - Parent MCQ ID
   * @param {string} questionData.question_text - Question text
   * @param {string} questionData.option_a - Option A
   * @param {string} questionData.option_b - Option B
   * @param {string} questionData.option_c - Option C
   * @param {string} questionData.option_d - Option D
   * @param {string} [questionData.option_e] - Option E (optional)
   * @param {string} questionData.correct_answer - Correct answer (A/B/C/D/E)
   * @param {string} [questionData.explanation] - Explanation (optional)
   * @param {number} [questionData.order] - Question order
   * @returns {Promise<Object>} Created question
   */
  createQuestion: async (questionData) => {
    const response = await api.post('/questions/', questionData);
    return response.data;
  },

  /**
   * Update an existing question (Admin only)
   * @param {number} id - Question ID
   * @param {Object} questionData - Updated question data
   * @returns {Promise<Object>} Updated question
   */
  updateQuestion: async (id, questionData) => {
    const response = await api.put(`/questions/${id}/`, questionData);
    return response.data;
  },

  /**
   * Delete a question (Admin only)
   * @param {number} id - Question ID
   * @returns {Promise<void>}
   */
  deleteQuestion: async (id) => {
    await api.delete(`/questions/${id}/`);
  },

  // ===== USER MCQ TAKING =====

  /**
   * Start an MCQ (get randomized questions without correct answers)
   * @param {number} mcqId - MCQ ID
   * @returns {Promise<Object>} MCQ with randomized questions
   */
  startMCQ: async (mcqId) => {
    const response = await api.get(`/mcqs/${mcqId}/start/`);
    return response.data;
  },

  /**
   * Create a new MCQ attempt
   * @param {number} mcqId - MCQ ID
   * @returns {Promise<Object>} Created attempt with attempt_id
   */
  startMCQAttempt: async (mcqId) => {
    const response = await api.post('/mcq-attempts/start/', { mcq_id: mcqId });
    return response.data;
  },

  /**
   * Submit MCQ answers
   * @param {number} attemptId - Attempt ID
   * @param {Array<Object>} answers - Array of {question_id, selected_answer}
   * @returns {Promise<Object>} MCQ results with scorecard
   */
  submitMCQAttempt: async (attemptId, answers) => {
    const response = await api.post(`/mcq-attempts/${attemptId}/submit/`, { answers });
    return response.data;
  },

  /**
   * Get MCQ results (scorecard)
   * @param {number} attemptId - Attempt ID
   * @returns {Promise<Object>} Detailed results with all answers
   */
  getMCQResults: async (attemptId) => {
    const response = await api.get(`/mcq-attempts/${attemptId}/results/`);
    return response.data;
  },

  /**
   * Get user's attempt history for an MCQ
   * @param {number} mcqId - MCQ ID
   * @returns {Promise<Array>} List of attempts
   */
  getMyMCQAttempts: async (mcqId) => {
    const response = await api.get(`/mcq-attempts/my-attempts/?mcq_id=${mcqId}`);
    return response.data;
  }
};

export default mcqService;
